// src/neetjee_guru/components/academicPlanner/TableViewport.js
import React, { Component } from "react";

const SUBJECT_COLORS = {
  math:    { bg: "#faf5ff", border: "#8b5cf6", text: "#6d28d9", label: "MATH" },
  physic:  { bg: "#eff6ff", border: "#3b82f6", text: "#1e40af", label: "PHY"  },
  chem:    { bg: "#f0fdf4", border: "#10b981", text: "#065f46", label: "CHEM" },
  botan:   { bg: "#ecfdf5", border: "#059669", text: "#047857", label: "BOT"  },
  zoolog:  { bg: "#fffbeb", border: "#f59e0b", text: "#92400e", label: "ZOO"  },
};

function getSubjectStyle(subject) {
  const s = (subject || "").trim().toLowerCase();
  for (const [key, val] of Object.entries(SUBJECT_COLORS)) {
    if (s.includes(key)) return val;
  }
  return { bg: "#f1f5f9", border: "#64748b", text: "#334155", label: subject.substring(0, 4).toUpperCase() };
}

function getStatusBadge(status) {
  if (status === "COMPLETED")
    return { bg: "#d1fae5", color: "#065f46", icon: "fal fa-check-circle", text: "Completed" };
  if (status === "IN_PROGRESS")
    return { bg: "#fef3c7", color: "#92400e", icon: "fal fa-spinner", text: "In Progress" };
  return { bg: "#e2e8f0", color: "#475569", icon: "fal fa-clock", text: "Pending" };
}

function getDayTypeStyle(type) {
  if (type === "Exam")    return { bg: "#eff6ff", color: "#1e40af", icon: "fal fa-file-alt",      text: "Exam"    };
  if (type === "Holiday") return { bg: "#fef2f2", color: "#991b1b", icon: "fal fa-umbrella-beach", text: "Holiday" };
  return                           { bg: "#f0fdf4", color: "#065f46", icon: "fal fa-chalkboard-teacher", text: "Lesson"  };
}

class TableViewport extends Component {
  render() {
    const { filteredDays, activeSubjects, onDayClick, selectedDay, searchText } = this.props;

    if (!filteredDays || filteredDays.length === 0) return null;

    // Expand rows: one row per subject per day
    const rows = [];
    filteredDays.forEach(day => {
      let subjectKeys = day.topics && Object.keys(day.topics).length > 0
        ? Object.keys(day.topics)
        : (activeSubjects && activeSubjects.length > 0 ? activeSubjects : []);

      // If activeSubjects filter is active, only show selected subjects
      if (activeSubjects && activeSubjects.length > 0) {
        subjectKeys = subjectKeys.filter(sub => activeSubjects.includes(sub));
      }

      const dayTypeMeta = getDayTypeStyle(day.type);

      if (subjectKeys.length === 0 || (day.type !== "Regular")) {
        rows.push({ day, subject: null, topic: day.title, status: null, rowspan: 1 });
      } else {
        subjectKeys.forEach((sub, idx) => {
          rows.push({
            day,
            subject: sub,
            topic: day.topics[sub] || "—",
            status: (day.teachingStatus && day.teachingStatus[sub]) || "PENDING",
            isFirst: idx === 0,
            totalSubs: subjectKeys.length
          });
        });
      }
    });

    const highlight = searchText ? searchText.toLowerCase() : "";

    function highlightText(text) {
      if (!highlight || !text) return text;
      const idx = text.toLowerCase().indexOf(highlight);
      if (idx === -1) return text;
      return (
        <>
          {text.substring(0, idx)}
          <mark style={{ background: "#fef08a", padding: 0 }}>{text.substring(idx, idx + highlight.length)}</mark>
          {text.substring(idx + highlight.length)}
        </>
      );
    }

    let prevDate = null;

    return (
      <div className="table-viewport-wrapper">
        <div style={{ overflowX: "auto", borderRadius: 14, boxShadow: "0 2px 16px rgba(0,0,0,0.07)", border: "1px solid #e2e8f0" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13.5px" }}>
            <thead>
              <tr style={{ background: "linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)", color: "#fff" }}>
                {["Day #", "Date", "Day", "Week", "Type", "Subject", "Topic / Title", "Status"].map(h => (
                  <th key={h} style={{
                    padding: "12px 14px",
                    fontWeight: 700,
                    fontSize: "11px",
                    textTransform: "uppercase",
                    letterSpacing: "0.6px",
                    whiteSpace: "nowrap",
                    borderBottom: "2px solid rgba(255,255,255,0.12)"
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const { day, subject, topic, status } = row;
                const isSelected = selectedDay && selectedDay.date === day.date;
                const isNewDate = day.date !== prevDate;
                prevDate = day.date;

                const dayTypeMeta = getDayTypeStyle(day.type);
                const subStyle = subject ? getSubjectStyle(subject) : null;
                const statusMeta = status ? getStatusBadge(status) : null;

                const bgRow = isSelected
                  ? "#f5f3ff"
                  : (day.type === "Holiday" ? "#fff5f5" : day.type === "Exam" ? "#f0f9ff" : (i % 2 === 0 ? "#ffffff" : "#f8fafc"));

                const dateObj = new Date(day.date);
                const formattedDate = isNaN(dateObj.getTime()) ? day.date : dateObj.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
                const dayName = isNaN(dateObj.getTime()) ? "" : dateObj.toLocaleDateString("en-IN", { weekday: "short" });
                const isSunday = dateObj.getDay() === 0;

                return (
                  <tr
                    key={`${day.date}-${subject || "nosubject"}-${i}`}
                    id={isNewDate ? `day-row-${day.date}` : `table-row-${day.date}-${i}`}
                    onClick={() => onDayClick && onDayClick(day)}
                    style={{
                      background: day.isToday && isNewDate
                        ? "#eef2ff"
                        : bgRow,
                      borderBottom: "1px solid #e9ecef",
                      borderLeft: isSelected
                        ? "4px solid #7c3aed"
                        : day.isToday && isNewDate
                          ? "4px solid #6366f1"
                          : isNewDate
                            ? "4px solid #e2e8f0"
                            : "4px solid transparent",
                      cursor: "pointer",
                      transition: "background 0.15s"
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = "#f5f3ff"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = day.isToday && isNewDate ? "#eef2ff" : bgRow; }}
                  >
                    {/* Day # */}
                    <td style={{ padding: "10px 14px", fontWeight: 700, color: isSunday ? "#ef4444" : "#1e1b4b", whiteSpace: "nowrap" }}>
                      {isNewDate ? (
                        <span style={{
                          display: "inline-flex", alignItems: "center", justifyContent: "center",
                          width: 34, height: 34, borderRadius: "50%",
                          background: isSelected ? "#7c3aed" : "#f1f5f9",
                          color: isSelected ? "#fff" : "#334155",
                          fontWeight: 800, fontSize: "13px"
                        }}>
                          {day.dayNumber || "—"}
                        </span>
                      ) : ""}
                    </td>

                    {/* Date */}
                    <td style={{ padding: "10px 14px", color: isSunday ? "#dc2626" : "#374151", fontWeight: isNewDate ? 700 : 400, whiteSpace: "nowrap" }}>
                      {isNewDate ? formattedDate : ""}
                    </td>

                    {/* Day Name */}
                    <td style={{ padding: "10px 14px", color: isSunday ? "#dc2626" : "#6b7280", fontWeight: 600, fontSize: "12px" }}>
                      {isNewDate ? dayName : ""}
                    </td>

                    {/* Week # */}
                    <td style={{ padding: "10px 14px", textAlign: "center" }}>
                      {isNewDate ? (
                        <span style={{ fontSize: "11px", color: "#94a3b8", fontWeight: 600 }}>
                          W{day.weekNumber}
                        </span>
                      ) : ""}
                    </td>

                    {/* Day Type */}
                    <td style={{ padding: "10px 14px" }}>
                      {isNewDate ? (
                        <span style={{
                          display: "inline-flex", alignItems: "center", gap: 4,
                          padding: "3px 10px", borderRadius: 20, fontSize: "11px", fontWeight: 700,
                          background: dayTypeMeta.bg, color: dayTypeMeta.color,
                          border: `1px solid ${dayTypeMeta.bg}`
                        }}>
                          <i className={dayTypeMeta.icon} style={{ fontSize: 10 }} />
                          {day.type === "Regular" ? "Lesson" : day.type}
                        </span>
                      ) : ""}
                    </td>

                    {/* Subject */}
                    <td style={{ padding: "10px 14px" }}>
                      {subject ? (
                        <span style={{
                          display: "inline-flex", alignItems: "center", gap: 5,
                          padding: "3px 10px", borderRadius: 20, fontSize: "11px", fontWeight: 700,
                          background: subStyle.bg, color: subStyle.text,
                          borderLeft: `3px solid ${subStyle.border}`
                        }}>
                          <span style={{ width: 6, height: 6, borderRadius: "50%", background: subStyle.border, flexShrink: 0 }} />
                          {subject}
                        </span>
                      ) : (
                        <span style={{ color: "#94a3b8", fontSize: "11px" }}>—</span>
                      )}
                    </td>

                    {/* Topic / Title */}
                    <td style={{ padding: "10px 14px", color: "#1e293b", fontWeight: subject ? 500 : 700, maxWidth: 360 }}>
                      <span style={{ lineHeight: 1.4 }}>
                        {highlightText(topic || "—")}
                      </span>
                    </td>

                    {/* Status */}
                    <td style={{ padding: "10px 14px" }}>
                      {statusMeta ? (
                        <span style={{
                          display: "inline-flex", alignItems: "center", gap: 4,
                          padding: "3px 10px", borderRadius: 20, fontSize: "11px", fontWeight: 700,
                          background: statusMeta.bg, color: statusMeta.color
                        }}>
                          <i className={statusMeta.icon} style={{ fontSize: 10 }} />
                          {statusMeta.text}
                        </span>
                      ) : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="d-flex justify-content-between align-items-center mt-2 px-1">
          <span style={{ fontSize: "12px", color: "#94a3b8" }}>
            <i className="fal fa-table mr-1" />
            {filteredDays.length} days · {rows.length} subject rows shown
          </span>
          <span style={{ fontSize: "12px", color: "#94a3b8" }}>
            Click any row to open the Detail Drawer
          </span>
        </div>
      </div>
    );
  }
}

export default TableViewport;
